import React, { useState, useEffect, lazy, Suspense } from "react";
import styled, { keyframes } from "styled-components";
import {
  Button,
  SecondaryButton,
  DangerButton,
} from "../../../styles/components/common/buttons";
import { pulseAnimation } from "../../../styles/components/common/animations";
import {
  FormGroup,
  Label,
  Input,
  Textarea,
} from "../../../styles/components/common/forms";
import { TabContainer, TabList, Tab, TabPanel } from "../../common/Tabs";
import {
  ProfileSection as BaseProfileSection,
  ItemContainer,
  ItemHeader,
  ItemTitle,
  ItemSubtitle,
  ItemContent,
} from "../../common/ProfileSection";
import ProfileNameWithBadge from "../../common/ProfileNameWithBadge";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaBriefcase,
  FaStar,
  FaEdit,
  FaSave,
  FaTimes,
} from "react-icons/fa";

// Animation keyframes
const fadeIn = keyframes`
  0% { 
    opacity: 0; 
    transform: translate(-50%, -50%) scale(0.9);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1.05);
  }
  100% { 
    opacity: 1; 
    transform: translate(-50%, -50%) scale(1);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const ProfileContainer = styled.div`
  width: 100%;
  animation: ${fadeInUp} 0.5s ease-out;
  will-change: transform, opacity;
`;

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(5px);
  z-index: 1000;
  
  &::after {
    content: '';
    width: 50px;
    height: 50px;
    border: 4px solid rgba(255, 255, 255, 0.1);
    border-top-color: rgba(99, 102, 241, 0.8);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 4px rgba(255, 255, 255, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    }
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StyledProfileSection = styled(BaseProfileSection)`
  animation: ${fadeInUp} 0.5s ease-out;
  will-change: transform, opacity;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const StyledTabPanel = styled(TabPanel)`
  animation: ${fadeInUp} 0.3s ease-out;
  will-change: transform, opacity;
`;

// Custom tab components
const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ProfileAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.8),
    rgba(79, 70, 229, 0.8)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  margin-right: 1.5rem;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  animation: ${pulseAnimation} 3s infinite ease-in-out;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileDetails = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const ProfileActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const StyledButton = styled(Button)`
  margin-left: 0.75rem;
`;

// Form components
const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  & > * {
    flex: 1;
  }
`;

const AddButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const RemoveButton = styled(DangerButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  margin-left: 0.5rem;
`;

// Filter components
const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 1.5rem;
  padding: 0.8rem;
  background: rgba(15, 23, 42, 0.3);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.1);
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
  padding: 0.3rem;
  border-radius: 6px;
`;

const FilterLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const FilterDateInput = styled.input`
  padding: 0.5rem 0.8rem;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  min-width: 140px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

const ClearButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: rgba(239, 68, 68, 0.8);
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  
  &:hover {
    background: rgba(239, 68, 68, 0.25);
    transform: translateY(-2px);
  }
`;

const FilterButton = styled(Button)`
  padding: 0.5rem 1rem;
  background: ${(props) =>
    props.active ? "rgba(99, 102, 241, 0.7)" : "rgba(15, 23, 42, 0.5)"};
  color: ${(props) =>
    props.active ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.7)"};
  border: 1px solid
    ${(props) =>
      props.active ? "rgba(99, 102, 241, 0.6)" : "rgba(99, 102, 241, 0.2)"};
  border-radius: 6px;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  font-weight: ${(props) => (props.active ? "500" : "normal")};
  box-shadow: ${(props) =>
    props.active ? "0 2px 8px rgba(99, 102, 241, 0.2)" : "none"};

  &:hover {
    background: rgba(99, 102, 241, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  }
`;

const CongratulatoryPrompt = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.95),
    rgba(79, 70, 229, 0.95)
  );
  color: white;
  padding: 2rem 3rem;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.4);
  z-index: 1000;
  animation: ${fadeIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-width: 300px;
  text-align: center;
  will-change: transform, opacity;

  h4 {
    font-size: 1.5rem;
    margin: 0 0 0.75rem 0;
  }

  p {
    font-size: 1.1rem;
    margin: 0;
    opacity: 0.9;
  }
`;

const FileInput = styled.input`
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`;

const FileLabel = styled.label`
  background: rgba(99, 102, 241, 0.1);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(99, 102, 241, 0.2);
  }
`;

const FilePreview = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

const StudentProfile = ({
  defaultTab = "experience",
  isProStudent = false,
}) => {
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isPro, setIsPro] = useState(isProStudent);
  const [showProPrompt, setShowProPrompt] = useState(false);
  const [editedProfile, setEditedProfile] = useState(null);

  // Add filter state for experience
  const [filterStatus, setFilterStatus] = useState("all"); // 'all', 'current', 'completed'
  const [filterStartDate, setFilterStartDate] = useState(""); // Date filter for experiences
  const [filterEndDate, setFilterEndDate] = useState(""); // Date filter for experiences

  // New state for form data
  const [formData, setFormData] = useState({
    name: "",
    major: "",
    semester: "",
    email: "",
    phone: "",
    cv: null,
    experience: [],
    activities: [],
    preferences: {
      jobTypes: [],
      locations: [],
      industries: [],
    },
  });

  // New state for new item forms
  const [newExperience, setNewExperience] = useState({
    company: "",
    position: "",
    startDate: "",
    endDate: "",
    description: "",
    isInternship: false,
    status: "completed",
  });

  const [newActivity, setNewActivity] = useState({
    name: "",
    role: "",
    startDate: "",
    endDate: "",
    description: "",
  });

  const [newJobType, setNewJobType] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newIndustry, setNewIndustry] = useState("");

  // Add loading state for tab changes
  const [isTabChanging, setIsTabChanging] = useState(false);

  // Add CompanyViews import at the top with other imports
  const CompanyViews = lazy(() => import("./components/CompanyViews"));

  useEffect(() => {
    // Fetch profile data
    const fetchProfile = async () => {
      try {
        // In a real app, this would be an API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock data based on student type
        const mockProfile = isProStudent
          ? {
              // Pro Student Profile (3+ months of internships)
              name: "John Doe",
              major: "Computer Science",
              semester: "Fall 2023",
              email: "john.doe@example.com",
              phone: "+1 (555) 123-4567",
              experience: [
                {
                  id: 1,
                  company: "Tech Solutions Inc.",
                  position: "Software Developer Intern",
                  startDate: "2023-06-01",
                  endDate: null, // Current internship with no end date
                  description:
                    "Developing and maintaining web applications using React and Node.js.",
                  isInternship: true,
                  status: "current",
                },
                {
                  id: 2,
                  company: "Digital Innovations",
                  position: "Frontend Development Intern",
                  startDate: "2023-09-01",
                  endDate: "2023-11-30",
                  description:
                    "Created responsive UI components and implemented user authentication.",
                  isInternship: true,
                  status: "completed",
                },
              ],
              activities: [
                {
                  id: 1,
                  name: "Hackathon 2023",
                  role: "Team Lead",
                  startDate: "2023-10-01",
                  endDate: "2023-10-03",
                  description:
                    "Led a team of 4 to develop a mobile app for campus navigation.",
                },
                {
                  id: 2,
                  name: "Coding Club",
                  role: "Member",
                  startDate: "2022-09-01",
                  endDate: "",
                  description:
                    "Participate in weekly coding challenges and workshops.",
                },
              ],
              preferences: {
                jobTypes: ["Internship", "Part-time"],
                locations: ["Remote", "New York", "San Francisco"],
                industries: ["Technology", "Finance", "Healthcare"],
              },
            }
          : {
              // Regular Student Profile (less than 3 months of internships)
              name: "John Doe",
              major: "Computer Science",
              semester: "Fall 2023",
              email: "john.doe@example.com",
              phone: "+1 (555) 123-4567",
              experience: [
                {
                  id: 1,
                  company: "Tech Solutions Inc.",
                  position: "Software Developer Intern",
                  startDate: "2023-06-01",
                  endDate: null, // Current internship with no end date
                  description:
                    "Assisted in developing and testing web applications using React.",
                  isInternship: true,
                  status: "current",
                },
                {
                  id: 2,
                  company: "Digital Innovations",
                  position: "Frontend Development Intern",
                  startDate: "2023-07-01",
                  endDate: "2023-07-31",
                  description:
                    "Helped create responsive UI components and implemented basic user interactions.",
                  isInternship: true,
                  status: "completed",
                },
              ],
              activities: [
                {
                  id: 1,
                  name: "Hackathon 2023",
                  role: "Team Lead",
                  startDate: "2023-10-01",
                  endDate: "2023-10-03",
                  description:
                    "Led a team of 4 to develop a mobile app for campus navigation.",
                },
                {
                  id: 2,
                  name: "Coding Club",
                  role: "Member",
                  startDate: "2022-09-01",
                  endDate: "",
                  description:
                    "Participate in weekly coding challenges and workshops.",
                },
              ],
              preferences: {
                jobTypes: ["Internship", "Part-time"],
                locations: ["Remote", "New York", "San Francisco"],
                industries: ["Technology", "Finance", "Healthcare"],
              },
            };

        setProfile(mockProfile);
        setFormData(mockProfile);

        console.log("isProStudent prop:", isProStudent);

        // Calculate if the student qualifies for pro status based on internship duration
        if (!isProStudent) {
          const hasProStatus = calculateProStatus(mockProfile.experience);
          console.log("Calculated pro status:", hasProStatus);
          setIsPro(hasProStatus);
        } else {
          // If isProStudent is true, always set isPro to true
          console.log("Setting isPro to true based on isProStudent prop");
          setIsPro(true);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
    // No loading state to manage anymore
  }, [isProStudent]);

  // Function to calculate if a student qualifies for pro status
  const calculateProStatus = (experiences) => {
    // Filter for internships that are completed (not current)
    const internships = experiences.filter(
      (exp) => exp.isInternship && exp.endDate && exp.status !== "current"
    );
    console.log("Filtered completed internships:", internships);

    // Calculate total internship duration in months
    let totalInternshipMonths = 0;

    internships.forEach((internship) => {
      const startDate = new Date(internship.startDate);
      const endDate = new Date(internship.endDate);

      // Calculate months between dates
      const months =
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth());

      // Add partial months
      const daysInMonth = 30;
      const partialMonth =
        (endDate.getDate() - startDate.getDate()) / daysInMonth;
      const totalMonths = months + partialMonth;

      console.log(
        `Internship at ${internship.company}: ${totalMonths.toFixed(2)} months`
      );
      totalInternshipMonths += totalMonths;
    });

    console.log("Total internship months:", totalInternshipMonths);
    // Return true if total internship duration is 3 months or more
    return totalInternshipMonths >= 3;
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    // In a real app, this would save changes to the backend
    setProfile(formData);

    // Check if the student qualifies for pro status after saving
    // Only check if they don't already have pro status
    if (!isPro) {
      console.log("Checking pro status on save...");
      const hasProStatus = calculateProStatus(formData.experience);
      console.log("Has pro status on save:", hasProStatus);

      if (hasProStatus) {
        console.log("Setting pro status to true on save");
        setIsPro(true);
        setShowProPrompt(true);
        // Hide the prompt after 5 seconds
        setTimeout(() => setShowProPrompt(false), 5000);
      }
    }

    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCVUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({
        ...formData,
        cv: e.target.files[0]
      });
    }
  };

  const handleRemoveCV = () => {
    setFormData({
      ...formData,
      cv: null
    });
  };

  const handleNewExperienceChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewExperience({
      ...newExperience,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleNewActivityChange = (e) => {
    const { name, value } = e.target;
    setNewActivity({
      ...newActivity,
      [name]: value,
    });
  };

  const addExperience = () => {
    if (!newExperience.company || !newExperience.position) return;

    // Create a copy of the experience to modify
    const updatedNewExperience = {
      ...newExperience,
    };

    // If status is set to current, ensure endDate is null
    if (updatedNewExperience.status === "current") {
      updatedNewExperience.endDate = null;
    } else {
      // Make sure there's a valid end date for completed experiences
      if (
        !updatedNewExperience.endDate ||
        updatedNewExperience.endDate === ""
      ) {
        updatedNewExperience.status = "current";
        updatedNewExperience.endDate = null;
      }
    }

    const newItem = {
      id: Date.now(),
      ...updatedNewExperience,
    };

    const updatedExperience = [...formData.experience, newItem];
    console.log("Adding new experience:", newItem);
    console.log("Updated experiences:", updatedExperience);

    // Check if this new experience makes the student eligible for pro status
    // Only completed internships count toward Pro badge
    if (
      !isPro &&
      newExperience.isInternship &&
      newExperience.status === "completed" &&
      newExperience.endDate
    ) {
      console.log("Checking pro status eligibility...");
      const hasProStatus = calculateProStatus(updatedExperience);
      console.log("Has pro status:", hasProStatus);

      if (hasProStatus) {
        console.log("Setting pro status to true");
        setIsPro(true);
        setShowProPrompt(true);
        // Hide the prompt after 5 seconds
        setTimeout(() => setShowProPrompt(false), 5000);
      }
    }

    setFormData({
      ...formData,
      experience: updatedExperience,
    });

    setNewExperience({
      company: "",
      position: "",
      startDate: "",
      endDate: "",
      description: "",
      isInternship: false,
      status: "completed",
    });
  };

  const addActivity = () => {
    if (!newActivity.name || !newActivity.role) return;

    const newItem = {
      id: Date.now(),
      ...newActivity,
    };

    setFormData({
      ...formData,
      activities: [...formData.activities, newItem],
    });

    setNewActivity({
      name: "",
      role: "",
      startDate: "",
      endDate: "",
      description: "",
    });
  };

  const addJobType = () => {
    if (!newJobType) return;
    
    // Check if job type already exists (from branch 1)
    if (formData.preferences.jobTypes.includes(newJobType)) {
      return; // Skip duplicate job types
    }
    
    // Add the new job type (from branch 2)
    setFormData({
      ...formData,
      preferences: {
        ...formData.preferences,
        jobTypes: [...formData.preferences.jobTypes, newJobType],
      },
    });

    setNewJobType("");
  };

  const addLocation = () => {
    if (!newLocation) return;
    
    // Check if location already exists
    if (formData.preferences.locations.includes(newLocation)) {
      return; // Skip duplicate locations
    }

    setFormData({
      ...formData,
      preferences: {
        ...formData.preferences,
        locations: [...formData.preferences.locations, newLocation],
      },
    });

    setNewLocation("");
  };

  const addIndustry = () => {
    if (!newIndustry) return;
    
    // Check if industry already exists
    if (formData.preferences.industries.includes(newIndustry)) {
      return; // Skip duplicate industries
    }

    setFormData({
      ...formData,
      preferences: {
        ...formData.preferences,
        industries: [...formData.preferences.industries, newIndustry],
      },
    });

    setNewIndustry("");
  };

  const removeExperience = (id) => {
    setFormData({
      ...formData,
      experience: formData.experience.filter((item) => item.id !== id),
    });
  };

  const removeActivity = (id) => {
    setFormData({
      ...formData,
      activities: formData.activities.filter((item) => item.id !== id),
    });
  };

  const removeJobType = (index) => {
    const newJobTypes = [...formData.preferences.jobTypes];
    newJobTypes.splice(index, 1);

    setFormData({
      ...formData,
      preferences: {
        ...formData.preferences,
        jobTypes: newJobTypes,
      },
    });
  };

  const removeLocation = (index) => {
    const newLocations = [...formData.preferences.locations];
    newLocations.splice(index, 1);

    setFormData({
      ...formData,
      preferences: {
        ...formData.preferences,
        locations: newLocations,
      },
    });
  };

  const removeIndustry = (index) => {
    const newIndustries = [...formData.preferences.industries];
    newIndustries.splice(index, 1);

    setFormData({
      ...formData,
      preferences: {
        ...formData.preferences,
        industries: newIndustries,
      },
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Present";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const calculateDuration = (startDate, endDate) => {
    // For current internships (no end date), don't calculate duration
    if (!endDate) {
      return "Current";
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate duration in months, rounding up to ensure 1 month is shown for any month-long internship
    const months = Math.ceil(
      (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth()) +
        (end.getDate() - start.getDate()) / 30
    );

    // Ensure minimum of 1 month is shown
    const adjustedMonths = Math.max(1, months);

    return adjustedMonths === 1 ? "1 month" : `${adjustedMonths} months`;
  };

  const formatDateRange = (startDate, endDate) => {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Function to determine if an experience is current
  const isCurrentExperience = (experience) => {
    return !experience.endDate || experience.status === "current";
  };

  const handleTabChange = (tab) => {
    setIsTabChanging(true);
    setActiveTab(tab);
    // Reset loading state after animation
    setTimeout(() => setIsTabChanging(false), 300);
  };

  console.log('Rendering with isPro state:', isPro);
  const tabs = ['experience', 'activities', 'preferences'];
  const tabNames = ['Experience', 'Activities', 'Job Preferences'];

  // Ensure profile is loaded before rendering
  if (!profile) {
    return null;
  }

  return (
    <ProfileContainer>
      {showProPrompt && (
        <CongratulatoryPrompt>
          <div>
            <h4 style={{ margin: "0 0 0.5rem 0" }}>Congratulations! 🎉</h4>
            <p style={{ margin: 0 }}>
              You've earned the Pro Student badge with 3+ months of internship
              experience!
            </p>
          </div>
        </CongratulatoryPrompt>
      )}

      <StyledProfileSection>
        <ProfileHeader>
          <ProfileAvatar>
            {profile.name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </ProfileAvatar>
          <ProfileInfo>
            {isEditing ? (
              <>
                <FormGroup>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="major">Major</Label>
                    <Input
                      type="text"
                      id="major"
                      name="major"
                      value={formData.major}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="semester">Semester</Label>
                    <Input
                      type="text"
                      id="semester"
                      name="semester"
                      value={formData.semester}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <Label>CV/Resume</Label>
                  <div>
                    <FileInput
                      type="file"
                      id="cv"
                      name="cv"
                      accept=".pdf,.doc,.docx"
                      onChange={handleCVUpload}
                    />
                    <FileLabel htmlFor="cv">
                      <span>📄</span> Upload CV
                    </FileLabel>
                    {formData.cv && (
                      <FilePreview>
                        {formData.cv.name}
                        <RemoveButton onClick={handleRemoveCV}>
                          Remove
                        </RemoveButton>
                      </FilePreview>
                    )}
                  </div>
                </FormGroup>
              </>
            ) : (
              <>
                <ProfileNameWithBadge name={profile.name} isPro={isPro} />
                <ProfileDetails>
                  {profile.major} • {profile.semester}
                </ProfileDetails>
                <ProfileDetails>
                  {profile.email} • {profile.phone}
                </ProfileDetails>
              </>
            )}
          </ProfileInfo>
        </ProfileHeader>

        <ProfileActions>
          {isEditing ? (
            <>
              <StyledButton onClick={handleSave} primary>
                Save Changes
              </StyledButton>
              <SecondaryButton onClick={handleEditToggle}>
                Cancel
              </SecondaryButton>
            </>
          ) : (
            <StyledButton onClick={handleEditToggle} primary>
              Edit Profile
            </StyledButton>
          )}
        </ProfileActions>
      </StyledProfileSection>

      <TabContainer>
        <TabList>
          {tabNames.map((name, index) => (
            <Tab
              key={tabs[index]}
              active={activeTab === tabs[index]}
              onClick={() => handleTabChange(tabs[index])}
            >
              {name}
            </Tab>
          ))}
        </TabList>

        {!isTabChanging && (
          <>
            <StyledTabPanel active={activeTab === "experience"}>
              {/* Filter buttons */}
              <div>
                <h4
                  style={{
                    marginBottom: "0.5rem",
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                  }}
                >
                  Filter Internships
                </h4>
                <FilterContainer>
                  {/* Status filter section */}
                  <FilterSection style={{ borderBottom: "1px solid rgba(99, 102, 241, 0.15)", paddingBottom: "0.6rem" }}>
                    <FilterLabel>Status:</FilterLabel>
                    <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                      <FilterButton
                        active={filterStatus === "all"}
                        onClick={() => setFilterStatus("all")}
                      >
                        All Internships
                      </FilterButton>
                      <FilterButton
                        active={filterStatus === "current"}
                        onClick={() => setFilterStatus("current")}
                      >
                        Current Intern
                      </FilterButton>
                      <FilterButton
                        active={filterStatus === "completed"}
                        onClick={() => setFilterStatus("completed")}
                      >
                        Internship Complete
                      </FilterButton>
                    </div>
                  </FilterSection>
                  
                  {/* Date filter section */}
                  <FilterSection style={{ 
                    background: (filterStartDate || filterEndDate) ? "rgba(99, 102, 241, 0.08)" : "transparent",
                    border: (filterStartDate || filterEndDate) ? "1px solid rgba(99, 102, 241, 0.2)" : "none"
                  }}>
                    <FilterLabel style={{
                      color: (filterStartDate || filterEndDate) ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.8)"
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" 
                        style={{ 
                          opacity: (filterStartDate || filterEndDate) ? 1 : 0.8,
                          color: (filterStartDate || filterEndDate) ? "rgba(99, 102, 241, 0.9)" : "currentColor"
                        }}>
                        <path d="M8 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M16 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M3 9H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                      Date Range:
                    </FilterLabel>
                    
                    <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <FilterLabel>From</FilterLabel>
                        <FilterDateInput
                          type="date"
                          value={filterStartDate}
                          onChange={(e) => setFilterStartDate(e.target.value)}
                          style={{
                            borderColor: filterStartDate ? "rgba(99, 102, 241, 0.5)" : "rgba(99, 102, 241, 0.3)",
                            background: filterStartDate ? "rgba(99, 102, 241, 0.1)" : "rgba(15, 23, 42, 0.7)"
                          }}
                        />
                      </div>
                      
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <FilterLabel>To</FilterLabel>
                        <FilterDateInput
                          type="date"
                          value={filterEndDate}
                          onChange={(e) => setFilterEndDate(e.target.value)}
                          style={{
                            borderColor: filterEndDate ? "rgba(99, 102, 241, 0.5)" : "rgba(99, 102, 241, 0.3)",
                            background: filterEndDate ? "rgba(99, 102, 241, 0.1)" : "rgba(15, 23, 42, 0.7)"
                          }}
                        />
                      </div>
                      
                      {(filterStartDate || filterEndDate) && (
                        <ClearButton
                          onClick={() => {
                            setFilterStartDate("");
                            setFilterEndDate("");
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Clear Dates
                        </ClearButton>
                      )}
                    </div>
                  </FilterSection>
                </FilterContainer>
              </div>

              {isEditing ? (
                <>
                  <StyledProfileSection>
                    <h3>Add New Experience</h3>
                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="company">Company</Label>
                        <Input
                          type="text"
                          id="company"
                          name="company"
                          value={newExperience.company}
                          onChange={handleNewExperienceChange}
                          placeholder="Company name"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label htmlFor="position">Position</Label>
                        <Input
                          type="text"
                          id="position"
                          name="position"
                          value={newExperience.position}
                          onChange={handleNewExperienceChange}
                          placeholder="Job title"
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="startDate">Start Date</Label>
                        <Input
                          type="date"
                          id="startDate"
                          name="startDate"
                          value={newExperience.startDate}
                          onChange={handleNewExperienceChange}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label htmlFor="endDate">End Date</Label>
                        <Input
                          type="date"
                          id="endDate"
                          name="endDate"
                          value={newExperience.endDate}
                          onChange={handleNewExperienceChange}
                          placeholder="Leave empty if current"
                        />
                      </FormGroup>
                    </FormRow>
                    <FormGroup>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={newExperience.description}
                        onChange={handleNewExperienceChange}
                        placeholder="Describe your responsibilities and achievements"
                      />
                    </FormGroup>
                    <FormRow>
                      <FormGroup style={{ display: "flex", gap: "1rem" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "0.6rem 1rem",
                            background: newExperience.isInternship
                              ? "rgba(99, 102, 241, 0.2)"
                              : "rgba(15, 23, 42, 0.5)",
                            borderRadius: "6px",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            border: `1px solid ${
                              newExperience.isInternship
                                ? "rgba(99, 102, 241, 0.6)"
                                : "rgba(99, 102, 241, 0.2)"
                            }`,
                          }}
                          onClick={() => {
                            const newIsInternship = !newExperience.isInternship;
                            setNewExperience({
                              ...newExperience,
                              isInternship: newIsInternship,
                            });
                          }}
                        >
                          <input
                            type="checkbox"
                            id="isInternship"
                            name="isInternship"
                            checked={newExperience.isInternship}
                            onChange={(e) => {
                              setNewExperience({
                                ...newExperience,
                                isInternship: e.target.checked,
                              });
                            }}
                            style={{
                              marginRight: "0.5rem",
                              width: "16px",
                              height: "16px",
                              accentColor: "#6366f1",
                            }}
                            onClick={(e) => e.stopPropagation()} // Prevent double toggling
                          />
                          <Label
                            htmlFor="isInternship"
                            style={{
                              margin: 0,
                              cursor: "pointer",
                              fontWeight: newExperience.isInternship
                                ? "500"
                                : "normal",
                              color: newExperience.isInternship
                                ? "rgba(255, 255, 255, 0.9)"
                                : "rgba(255, 255, 255, 0.7)",
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent double toggling
                              const newIsInternship =
                                !newExperience.isInternship;
                              setNewExperience({
                                ...newExperience,
                                isInternship: newIsInternship,
                              });
                            }}
                          >
                            This is an internship
                          </Label>
                        </div>

                        {newExperience.isInternship && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "0.6rem 1rem",
                              background:
                                newExperience.status === "current"
                                  ? "rgba(99, 102, 241, 0.2)"
                                  : "rgba(15, 23, 42, 0.5)",
                              borderRadius: "6px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              border: `1px solid ${
                                newExperience.status === "current"
                                  ? "rgba(99, 102, 241, 0.6)"
                                  : "rgba(99, 102, 241, 0.2)"
                              }`,
                            }}
                            onClick={() => {
                              const newStatus =
                                newExperience.status === "current"
                                  ? "completed"
                                  : "current";
                              setNewExperience({
                                ...newExperience,
                                endDate:
                                  newStatus === "current" ? "" : "yyyy-MM-dd",
                                status: newStatus,
                              });
                            }}
                          >
                            <input
                              type="checkbox"
                              id="isCurrent"
                              name="isCurrent"
                              checked={newExperience.status === "current"}
                              onChange={(e) => {
                                setNewExperience({
                                  ...newExperience,
                                  endDate: e.target.checked ? "" : "yyyy-MM-dd",
                                  status: e.target.checked
                                    ? "current"
                                    : "completed",
                                });
                              }}
                              style={{
                                marginRight: "0.5rem",
                                width: "16px",
                                height: "16px",
                                accentColor: "#6366f1",
                              }}
                              onClick={(e) => e.stopPropagation()} // Prevent double toggling
                            />
                            <Label
                              htmlFor="isCurrent"
                              style={{
                                margin: 0,
                                cursor: "pointer",
                                fontWeight:
                                  newExperience.status === "current"
                                    ? "500"
                                    : "normal",
                                color:
                                  newExperience.status === "current"
                                    ? "rgba(255, 255, 255, 0.9)"
                                    : "rgba(255, 255, 255, 0.7)",
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent double toggling
                                const newStatus =
                                  newExperience.status === "current"
                                    ? "completed"
                                    : "current";
                                setNewExperience({
                                  ...newExperience,
                                  endDate:
                                    newStatus === "current" ? "" : "yyyy-MM-dd",
                                  status: newStatus,
                                });
                              }}
                            >
                              This is a current internship
                            </Label>
                          </div>
                        )}
                      </FormGroup>
                    </FormRow>
                    <AddButton
                      onClick={addExperience}
                      style={{
                        background: "rgba(99, 102, 241, 0.7)",
                        color: "white",
                        border: "1px solid rgba(99, 102, 241, 0.3)",
                        borderRadius: "6px",
                        padding: "0.6rem 1.2rem",
                        transition: "all 0.3s ease",
                        marginTop: "1.5rem",
                        fontWeight: "500",
                      }}
                    >
                      Add Experience
                    </AddButton>
                  </StyledProfileSection>

                  <h3>Your Experience</h3>
                  {formData.experience
                    .filter((exp) => {
                      // Status filter
                      const statusMatch = 
                        filterStatus === "all" || 
                        (filterStatus === "current" && isCurrentExperience(exp)) || 
                        (filterStatus === "completed" && !isCurrentExperience(exp));
                      
                      // Date filter
                      const startDate = filterStartDate ? new Date(filterStartDate) : null;
                      const endDate = filterEndDate ? new Date(filterEndDate) : null;
                      
                      const expStartDate = new Date(exp.startDate);
                      const expEndDate = exp.endDate ? new Date(exp.endDate) : new Date();
                      
                      const dateMatch = 
                        (!startDate || expStartDate >= startDate) && 
                        (!endDate || expEndDate <= endDate);
                      
                      return statusMatch && dateMatch;
                    })
                    .map((exp) => (
                      <ItemContainer key={exp.id}>
                        <ItemHeader>
                          <ItemTitle>
                            {exp.position}
                            {exp.isInternship && (
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  marginLeft: "0.5rem",
                                  color: "rgba(99, 102, 241, 0.9)",
                                }}
                              >
                                (Internship)
                              </span>
                            )}
                          </ItemTitle>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                            }}
                          >
                            {exp.isInternship && !isCurrentExperience(exp) && (
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  color: "rgba(255, 255, 255, 0.7)",
                                  background: "rgba(99, 102, 241, 0.2)",
                                  padding: "0.25rem 0.5rem",
                                  borderRadius: "4px",
                                }}
                              >
                                {calculateDuration(exp.startDate, exp.endDate)}
                              </span>
                            )}
                            {isCurrentExperience(exp) && (
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  color: "white",
                                  background: "rgba(99, 102, 241, 0.7)",
                                  padding: "0.25rem 0.5rem",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "6px",
                                    height: "6px",
                                    background: "rgba(255, 255, 255, 0.9)",
                                    borderRadius: "50%",
                                    animation: "pulse 1.5s infinite",
                                  }}
                                ></span>
                                Current
                              </span>
                            )}
                            <RemoveButton
                              onClick={() => removeExperience(exp.id)}
                            >
                              Remove
                            </RemoveButton>
                          </div>
                        </ItemHeader>
                        <ItemSubtitle>
                          {exp.company} •{" "}
                          {formatDateRange(exp.startDate, exp.endDate)}
                        </ItemSubtitle>
                        <ItemContent>{exp.description}</ItemContent>
                      </ItemContainer>
                    ))}
                </>
              ) : (
                profile.experience
                  .filter((exp) => {
                    // Status filter
                    const statusMatch = 
                      filterStatus === "all" || 
                      (filterStatus === "current" && isCurrentExperience(exp)) || 
                      (filterStatus === "completed" && !isCurrentExperience(exp));
                    
                    // Date filter
                    const startDate = filterStartDate ? new Date(filterStartDate) : null;
                    const endDate = filterEndDate ? new Date(filterEndDate) : null;
                    
                    const expStartDate = new Date(exp.startDate);
                    const expEndDate = exp.endDate ? new Date(exp.endDate) : new Date();
                    
                    const dateMatch = 
                      (!startDate || expStartDate >= startDate) && 
                      (!endDate || expEndDate <= endDate);
                    
                    return statusMatch && dateMatch;
                  })
                  .map((exp) => (
                    <StyledProfileSection key={exp.id}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <h3>
                          {exp.position}
                          {exp.isInternship && (
                            <span
                              style={{
                                fontSize: "0.8rem",
                                marginLeft: "0.5rem",
                                color: "rgba(99, 102, 241, 0.9)",
                              }}
                            >
                              (Internship)
                            </span>
                          )}
                        </h3>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          {exp.isInternship && !isCurrentExperience(exp) && (
                            <span
                              style={{
                                fontSize: "0.8rem",
                                color: "rgba(255, 255, 255, 0.7)",
                                background: "rgba(99, 102, 241, 0.2)",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "4px",
                              }}
                            >
                              {calculateDuration(exp.startDate, exp.endDate)}
                            </span>
                          )}
                          {isCurrentExperience(exp) && (
                            <span
                              style={{
                                fontSize: "0.8rem",
                                color: "white",
                                background: "rgba(99, 102, 241, 0.7)",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "6px",
                                  height: "6px",
                                  background: "rgba(255, 255, 255, 0.9)",
                                  borderRadius: "50%",
                                  animation: "pulse 1.5s infinite",
                                }}
                              ></span>
                              Current
                            </span>
                          )}
                        </div>
                      </div>
                      <p>
                        {exp.company} •{" "}
                        {formatDateRange(exp.startDate, exp.endDate)}
                      </p>
                      <p>{exp.description}</p>
                    </StyledProfileSection>
                  ))
              )}
            </StyledTabPanel>

            <StyledTabPanel active={activeTab === "activities"}>
              {isEditing ? (
                <>
                  <StyledProfileSection>
                    <h3>Add New Activity</h3>
                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="activityName">Activity Name</Label>
                        <Input
                          type="text"
                          id="activityName"
                          name="name"
                          value={newActivity.name}
                          onChange={handleNewActivityChange}
                          placeholder="Activity name"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label htmlFor="activityRole">Your Role</Label>
                        <Input
                          type="text"
                          id="activityRole"
                          name="role"
                          value={newActivity.role}
                          onChange={handleNewActivityChange}
                          placeholder="Your role in the activity"
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="activityStartDate">Start Date</Label>
                        <Input
                          type="date"
                          id="activityStartDate"
                          name="startDate"
                          value={newActivity.startDate}
                          onChange={handleNewActivityChange}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label htmlFor="activityEndDate">End Date</Label>
                        <Input
                          type="date"
                          id="activityEndDate"
                          name="endDate"
                          value={newActivity.endDate}
                          onChange={handleNewActivityChange}
                          placeholder="Leave empty if ongoing"
                        />
                      </FormGroup>
                    </FormRow>
                    <FormGroup>
                      <Label htmlFor="activityDescription">Description</Label>
                      <Textarea
                        id="activityDescription"
                        name="description"
                        value={newActivity.description}
                        onChange={handleNewActivityChange}
                        placeholder="Describe the activity and your involvement"
                      />
                    </FormGroup>
                    <AddButton onClick={addActivity}>Add Activity</AddButton>
                  </StyledProfileSection>

                  <h3>Your Activities</h3>
                  {formData.activities.map((activity) => (
                    <ItemContainer key={activity.id}>
                      <ItemHeader>
                        <ItemTitle>{activity.name}</ItemTitle>
                        <RemoveButton
                          onClick={() => removeActivity(activity.id)}
                        >
                          Remove
                        </RemoveButton>
                      </ItemHeader>
                      <ItemSubtitle>
                        {activity.role} •{" "}
                        {formatDateRange(activity.startDate, activity.endDate)}
                      </ItemSubtitle>
                      <ItemContent>{activity.description}</ItemContent>
                    </ItemContainer>
                  ))}
                </>
              ) : (
                profile.activities.map((activity) => (
                  <StyledProfileSection key={activity.id}>
                    <h3>{activity.name}</h3>
                    <p>
                      {activity.role} •{" "}
                      {formatDateRange(activity.startDate, activity.endDate)}
                    </p>
                    <p>{activity.description}</p>
                  </StyledProfileSection>
                ))
              )}
            </StyledTabPanel>

            <StyledTabPanel active={activeTab === "preferences"}>
              {isEditing ? (
                <StyledProfileSection>
                  <h3>Job Types</h3>
                  <FormGroup>
                    <Label htmlFor="jobType">Add Job Type</Label>
                    <div style={{ display: "flex" }}>
                      <Input
                        type="text"
                        id="jobType"
                        value={newJobType}
                        onChange={(e) => setNewJobType(e.target.value)}
                        placeholder="e.g., Full-time, Part-time, Internship"
                      />
                      <AddButton
                        onClick={addJobType}
                        style={{ marginLeft: "0.5rem", marginTop: 0 }}
                      >
                        Add
                      </AddButton>
                    </div>
                  </FormGroup>

                  {formData.preferences.jobTypes.map((type, index) => (
                    <ItemContainer key={index}>
                      <ItemHeader>
                        <ItemTitle>{type}</ItemTitle>
                        <RemoveButton onClick={() => removeJobType(index)}>
                          Remove
                        </RemoveButton>
                      </ItemHeader>
                    </ItemContainer>
                  ))}

                  <h3>Preferred Locations</h3>
                  <FormGroup>
                    <Label htmlFor="location">Add Location</Label>
                    <div style={{ display: "flex" }}>
                      <Input
                        type="text"
                        id="location"
                        value={newLocation}
                        onChange={(e) => setNewLocation(e.target.value)}
                        placeholder="e.g., New York, Remote, San Francisco"
                      />
                      <AddButton
                        onClick={addLocation}
                        style={{ marginLeft: "0.5rem", marginTop: 0 }}
                      >
                        Add
                      </AddButton>
                    </div>
                  </FormGroup>

                  {formData.preferences.locations.map((location, index) => (
                    <ItemContainer key={index}>
                      <ItemHeader>
                        <ItemTitle>{location}</ItemTitle>
                        <RemoveButton onClick={() => removeLocation(index)}>
                          Remove
                        </RemoveButton>
                      </ItemHeader>
                    </ItemContainer>
                  ))}

                  <h3>Industries</h3>
                  <FormGroup>
                    <Label htmlFor="industry">Add Industry</Label>
                    <div style={{ display: "flex" }}>
                      <Input
                        type="text"
                        id="industry"
                        value={newIndustry}
                        onChange={(e) => setNewIndustry(e.target.value)}
                        placeholder="e.g., Technology, Finance, Healthcare"
                      />
                      <AddButton
                        onClick={addIndustry}
                        style={{ marginLeft: "0.5rem", marginTop: 0 }}
                      >
                        Add
                      </AddButton>
                    </div>
                  </FormGroup>

                  {formData.preferences.industries.map((industry, index) => (
                    <ItemContainer key={index}>
                      <ItemHeader>
                        <ItemTitle>{industry}</ItemTitle>
                        <RemoveButton onClick={() => removeIndustry(index)}>
                          Remove
                        </RemoveButton>
                      </ItemHeader>
                    </ItemContainer>
                  ))}
                </StyledProfileSection>
              ) : (
                <StyledProfileSection>
                  <h3>Job Types</h3>
                  <p>{profile.preferences.jobTypes.join(", ")}</p>

                  <h3>Preferred Locations</h3>
                  <p>{profile.preferences.locations.join(", ")}</p>

                  <h3>Industries</h3>
                  <p>{profile.preferences.industries.join(", ")}</p>
                </StyledProfileSection>
              )}
            </StyledTabPanel>

            <StyledTabPanel active={activeTab === 'views'}>
              <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading company views...</div>}>
                <CompanyViews />
              </Suspense>
            </StyledTabPanel>
          </>
        )}
      </TabContainer>
    </ProfileContainer>
  );
};

export default StudentProfile;
